import type React from 'react';
import { Dialog } from '@/components/ui/dialog';
import type { EmiHistoryItem } from './types';
import { formatCurrency, getModeTitle } from './calculations';
import { formatHistoryDate } from './history';

export interface HistoryModalProps {
  open: boolean;
  onClose: () => void;
  history: EmiHistoryItem[];
  onSelect: (item: EmiHistoryItem) => void;
  onDelete: (id: string) => void;
  onClearAll: () => void;
}

export const HistoryModal: React.FC<HistoryModalProps> = ({
  open,
  onClose,
  history,
  onSelect,
  onDelete,
  onClearAll,
}) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Calculation History"
      closeOnBackdropClick={true}
      className="max-w-2xl sm:max-w-2xl"
    >
      <div className="space-y-4">
        {/* Header Summary & Clear All */}
        {history.length > 0 && (
          <div className="flex items-center justify-between pb-2 border-b border-gray-150">
            <span className="text-xs font-semibold text-gray-500">
              {history.length} calculation{history.length > 1 ? 's' : ''} stored locally
            </span>
            <button
              type="button"
              onClick={onClearAll}
              className="text-xs font-bold text-red-600 hover:text-red-700 hover:underline inline-flex items-center gap-1 cursor-pointer"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                />
              </svg>
              Clear All History
            </button>
          </div>
        )}

        {/* Empty State */}
        {history.length === 0 ? (
          <div className="py-12 text-center space-y-3">
            <div className="w-14 h-14 mx-auto rounded-2xl bg-brand-50 border border-brand-200 flex items-center justify-center text-brand-700">
              <svg
                className="w-7 h-7"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <div>
              <p className="text-sm font-extrabold text-gray-800">No Calculation History Yet</p>
              <p className="text-xs text-gray-500 mt-1 max-w-sm mx-auto">
                Whenever you calculate a loan EMI, loan amount, interest rate, or duration, it will
                automatically be saved here for quick reference.
              </p>
            </div>
          </div>
        ) : (
          <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
            {history.map((item) => (
              <div
                key={item.id}
                className="p-4 rounded-2xl border border-gray-200 hover:border-brand-300 bg-white hover:bg-brand-50/20 transition-all shadow-2xs space-y-3"
              >
                {/* Top Row: Mode badge, Date, and Delete Button */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-[11px] font-extrabold px-2.5 py-0.5 rounded-full bg-brand-100 text-brand-800 border border-brand-200">
                      Solved: {getModeTitle(item.mode)}
                    </span>
                    <span className="text-[11px] text-gray-400 font-medium">
                      {formatHistoryDate(item.timestamp)}
                    </span>
                  </div>

                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      onDelete(item.id);
                    }}
                    title="Delete this record"
                    className="p-1 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors cursor-pointer"
                  >
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                      />
                    </svg>
                  </button>
                </div>

                {/* Metrics Breakdown Grid */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 p-3 rounded-xl bg-gray-50 border border-gray-150 text-xs">
                  <div>
                    <span className="text-[10px] text-gray-400 font-medium block uppercase tracking-wider">
                      Principal
                    </span>
                    <span className="font-extrabold text-gray-900">
                      {formatCurrency(item.principal)}
                    </span>
                  </div>
                  <div>
                    <span className="text-[10px] text-gray-400 font-medium block uppercase tracking-wider">
                      Rate (% p.a.)
                    </span>
                    <span className="font-extrabold text-gray-900">{item.rate.toFixed(2)}%</span>
                  </div>
                  <div>
                    <span className="text-[10px] text-gray-400 font-medium block uppercase tracking-wider">
                      Duration
                    </span>
                    <span className="font-extrabold text-gray-900">
                      {item.totalMonths} Mos ({item.years}Y{' '}
                      {item.months > 0 ? `${item.months}M` : ''})
                    </span>
                  </div>
                  <div>
                    <span className="text-[10px] text-brand-700 font-semibold block uppercase tracking-wider">
                      Monthly EMI
                    </span>
                    <span className="font-extrabold text-brand-800">
                      {formatCurrency(item.emi)}
                    </span>
                  </div>
                </div>

                {/* Bottom Row: Totals & Load Button */}
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 pt-1 border-t border-gray-100">
                  <div className="flex items-center gap-3 text-xs text-gray-500">
                    <span>
                      Interest:{' '}
                      <strong className="text-amber-700 font-extrabold">
                        {formatCurrency(item.totalInterest)}
                      </strong>
                    </span>
                    <span>•</span>
                    <span>
                      Total:{' '}
                      <strong className="text-gray-900 font-extrabold">
                        {formatCurrency(item.totalAmount)}
                      </strong>
                    </span>
                  </div>

                  <button
                    type="button"
                    onClick={() => onSelect(item)}
                    className="inline-flex items-center justify-center gap-1.5 px-3 py-1.5 bg-brand-50 hover:bg-brand-100 text-brand-800 border border-brand-200 rounded-xl text-xs font-extrabold transition-all active:scale-95 cursor-pointer"
                  >
                    <svg
                      className="w-3.5 h-3.5 text-brand-700"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3"
                      />
                    </svg>
                    <span>Load into Calculator</span>
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Dialog>
  );
};
